import { useEffect, useState } from "react";
import api, { rupiah, errMsg } from "../lib/api";
import { Package, Wallet, AlertTriangle, ShoppingCart, ArrowDownLeft, ArrowUpRight, RotateCcw, TrendingUp, Store, Coins, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend } from "recharts";
import { useAuth } from "../context/AuthContext";

const CH_COLORS = { Offline: "#2A9D8F", Shopee: "#EE4D2D", Tokopedia: "#42B549", "TikTok Shop": "#0A0A0A", Lazada: "#0F146D", Lainnya: "#E9C46A" };

const typeMeta = {
  masuk: { label: "Masuk", color: "var(--masuk)", icon: ArrowDownLeft },
  keluar: { label: "Keluar", color: "var(--keluar)", icon: ArrowUpRight },
  retur: { label: "Retur", color: "var(--biru)", icon: RotateCcw },
};

const Stat = ({ label, value, icon: Icon, color, testid }) => (
  <div data-testid={testid} className="nb-card nb-hover p-5" style={{ background: color || "#fff" }}>
    <div className="flex items-start justify-between">
      <span className="label-tag">{label}</span>
      <Icon className="w-5 h-5" strokeWidth={2.4} />
    </div>
    <div className="mt-3 text-2xl sm:text-3xl font-display font-extrabold break-words">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const canFinance = !!(user?.is_owner || user?.perms?.keuangan);
  const [d, setD] = useState(null);
  const [chart, setChart] = useState([]);
  const [channels, setChannels] = useState([]);
  const [chMonth, setChMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dayDate, setDayDate] = useState(new Date().toISOString().slice(0, 10));
  const [dayData, setDayData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    if (canFinance) {
      api.get("/dashboard/sales-chart?days=14").then((r) => setChart(
        r.data.map((x) => ({ ...x, label: x.date.slice(5) }))
      )).catch(() => {});
    }
  }, [canFinance]);

  useEffect(() => {
    if (!canFinance) return;
    api.get(`/dashboard/channels?month=${chMonth}`).then((r) => setChannels(r.data.channels || [])).catch(() => {});
  }, [chMonth, canFinance]);

  useEffect(() => {
    if (!canFinance) return;
    api.get(`/dashboard/day-profit?date=${dayDate}`).then((r) => setDayData(r.data)).catch(() => setDayData(null));
  }, [dayDate, canFinance]);

  if (!d) return <div className="text-[#52525B]">Memuat…</div>;

  return (
    <div className="space-y-6 animate-fadeup">
      <div className="nb-card p-4 flex items-center justify-center" data-testid="store-logo">
        <img src="/logo.png" alt="Mainan Dede" className="h-20 sm:h-24 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Dasbor</h1>
        <p className="text-[#52525B] text-sm">Ringkasan stok & penjualan hari ini</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        <Stat testid="stat-total-items" label="Total Barang" value={d.total_items} icon={Package} />
        {d.stock_value != null && <Stat testid="stat-stock-value" label="Nilai Stok" value={rupiah(d.stock_value)} icon={Wallet} color="var(--primary)" />}
        <Stat testid="stat-low-stock" label="Stok Tipis" value={d.low_stock_count} icon={AlertTriangle} color={d.low_stock_count ? "var(--retur)" : "#fff"} />
        {canFinance && d.today_sales_total != null && <Stat testid="stat-today-sales" label="Penjualan Hari Ini" value={rupiah(d.today_sales_total)} icon={ShoppingCart} color="var(--masuk)" />}
        {canFinance && d.today_profit_total != null && <Stat testid="stat-today-profit" label="Profit Hari Ini" value={rupiah(d.today_profit_total)} icon={Coins} color="var(--primary)" />}
        {canFinance && d.month_profit != null && <Stat testid="stat-month-profit" label={`Profit Bulan Ini${d.month_margin != null ? ` · ${d.month_margin}%` : ""}`} value={rupiah(d.month_profit)} icon={TrendingUp} color="var(--masuk)" />}
      </div>

      {canFinance && (<>
      <div className="nb-card p-5" data-testid="day-profit-card" style={{ background: "var(--primary)" }}>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" strokeWidth={2.5} />
            <h2 className="font-display font-bold text-lg">Profit per Tanggal</h2>
          </div>
          <input data-testid="day-profit-date" type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} className="nb-input w-auto bg-white" />
        </div>
        {dayData ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="day-omzet">
              <span className="label-tag">Penjualan (bersih)</span>
              <div className="font-display font-extrabold text-2xl mt-1">{rupiah(dayData.omzet)}</div>
              <p className="text-xs text-[#52525B] mt-0.5">{dayData.count} nota</p>
            </div>
            <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="day-profit">
              <span className="label-tag">Profit</span>
              <div className="font-display font-extrabold text-2xl mt-1" style={{ color: dayData.profit >= 0 ? "var(--masuk)" : "var(--retur)" }}>{rupiah(dayData.profit)}</div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#3f3f46]">Memuat…</p>
        )}
      </div>

      <div className="nb-card p-5" data-testid="sales-chart">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5" strokeWidth={2.5} />
          <h2 className="font-display font-bold text-lg">Grafik Penjualan (14 hari)</h2>
        </div>
        <div className="w-full h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0A0A0A20" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#52525B" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: "#52525B" }} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
              <Tooltip formatter={(v) => rupiah(v)} labelFormatter={(l) => `Tgl ${l}`} contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="total" fill="#E9C46A" stroke="#0A0A0A" strokeWidth={2} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="nb-card p-5" data-testid="channel-chart">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5" strokeWidth={2.5} />
            <h2 className="font-display font-bold text-lg">Omzet per Channel</h2>
          </div>
          <input data-testid="channel-month" type="month" value={chMonth} onChange={(e) => setChMonth(e.target.value)} className="nb-input w-auto" />
        </div>
        {channels.length === 0 ? (
          <p className="text-sm text-[#52525B]">Belum ada penjualan pada bulan ini.</p>
        ) : (
          <div className="w-full" style={{ height: Math.max(180, channels.length * 72) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={channels} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#0A0A0A20" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#52525B" }} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                <YAxis type="category" dataKey="tipe" tick={{ fontSize: 12, fill: "#0A0A0A" }} width={95} />
                <Tooltip formatter={(v, n) => [rupiah(v), n === "profit" ? "Profit" : "Omzet"]} contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(val) => (val === "profit" ? "Profit" : "Omzet")} />
                <Bar dataKey="omzet" name="omzet" stroke="#0A0A0A" strokeWidth={2} radius={[0, 4, 4, 0]}>
                  {channels.map((c, i) => <Cell key={i} fill={CH_COLORS[c.tipe] || "#E9C46A"} />)}
                </Bar>
                <Bar dataKey="profit" name="profit" fill="#457B9D" stroke="#0A0A0A" strokeWidth={2} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      </>)}

      <div className="grid md:grid-cols-2 gap-6 items-start">
        <div className="nb-card p-5" data-testid="low-stock-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--retur)" }} strokeWidth={2.5} />
            <h2 className="font-display font-bold text-lg">Peringatan Stok Tipis</h2>
          </div>
          {d.low_stock_items.length === 0 ? (
            <p className="text-sm text-[#52525B]">Semua stok aman 🎉</p>
          ) : (
            <div className="space-y-2">
              {d.low_stock_items.map((i) => (
                <div key={i.id} className="flex items-center justify-between border-2 border-[#0A0A0A] rounded-lg px-3 py-2">
                  <div>
                    <div className="font-bold text-sm">{i.name}</div>
                    <div className="label-tag">{i.category}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-extrabold" style={{ color: "var(--retur)" }}>{i.stock} {i.unit}</div>
                    <div className="text-xs text-[#52525B]">min {i.min_stock}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="nb-card p-5" data-testid="recent-txn-panel">
          <h2 className="font-display font-bold text-lg mb-4">Transaksi Terbaru</h2>
          {d.recent_transactions.length === 0 ? (
            <p className="text-sm text-[#52525B]">Belum ada transaksi.</p>
          ) : (
            <div className="space-y-2">
              {d.recent_transactions.map((t) => {
                const m = typeMeta[t.type] || typeMeta.masuk;
                return (
                  <div key={t.id} className="flex items-center gap-3 border-2 border-[#0A0A0A] rounded-lg px-3 py-2">
                    <div className="w-8 h-8 rounded-md border-2 border-[#0A0A0A] flex items-center justify-center shrink-0" style={{ background: m.color }}>
                      <m.icon className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{t.item_name}</div>
                      <div className="label-tag">{m.label} • {t.date}</div>
                    </div>
                    <div className="font-display font-extrabold">{t.qty}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
